import { DIRECTORY_BOOKING_CATEGORY_IDS } from './businessDirectoryTaxonomy';

/** How a business appears on ShopGND (shop catalog, instant booking, quote-based hire). */
export const BUSINESS_MODES = ['shop', 'book', 'hire', 'listings'] as const;
export type BusinessMode = (typeof BUSINESS_MODES)[number];

export const BUSINESS_MODE_LABELS: Record<BusinessMode, string> = {
  shop: 'Shop',
  book: 'Book',
  hire: 'Hire',
  listings: 'Properties',
};

/** Vendor onboarding `businessType` values. */
export const VENDOR_BUSINESS_TYPES = [
  'retail',
  'food',
  'fashion',
  'electronics',
  'home',
  'beauty',
  'salon',
  'barber',
  'spa',
  'agriculture',
  'crafts',
  'trades',
  'real_estate',
  'other',
] as const;
export type VendorBusinessType = (typeof VENDOR_BUSINESS_TYPES)[number];

export const VENDOR_BUSINESS_TYPE_LABELS: Record<VendorBusinessType, string> = {
  retail: 'Retail & products',
  food: 'Food & beverages',
  fashion: 'Fashion & clothing',
  electronics: 'Electronics & tech',
  home: 'Home & kitchen',
  beauty: 'Beauty & health',
  salon: 'Salon',
  barber: 'Barber',
  spa: 'Spa & wellness',
  agriculture: 'Agriculture & farm',
  crafts: 'Arts, crafts & gifts',
  trades: 'Trades & contractors',
  real_estate: 'Real estate & property',
  other: 'Other',
};

/** Default modes when `businessModes` is unset (inferred from directory category or businessType). */
export function defaultModesForDirectoryCategory(directoryCategoryId?: string): BusinessMode[] {
  const id = directoryCategoryId?.trim();
  if (!id) return ['shop'];
  if (DIRECTORY_BOOKING_CATEGORY_IDS.has(id)) return ['book', 'hire'];
  if (id.startsWith('pro-')) return ['hire'];
  if (id.startsWith('prop-')) return ['listings'];
  if (id.startsWith('beauty-')) return ['book'];
  if (id.startsWith('svc-')) return ['hire', 'book'];
  if (id.startsWith('shop-')) return ['shop'];
  return ['shop'];
}

/** Default modes when `businessModes` is unset (inferred from businessType + listings). */
export function defaultModesForBusinessType(
  businessType?: string,
  directoryCategoryId?: string,
): BusinessMode[] {
  if (directoryCategoryId) {
    return defaultModesForDirectoryCategory(directoryCategoryId);
  }
  const t = (businessType ?? '').toLowerCase();
  if (t === 'salon' || t === 'barber' || t === 'spa' || t === 'beauty') return ['book', 'shop'];
  if (t === 'trades') return ['hire'];
  if (t === 'real_estate') return ['listings'];
  if (t === 'food') return ['shop'];
  return ['shop'];
}

export function primaryModeForBusinessType(businessType?: string): BusinessMode {
  const modes = defaultModesForBusinessType(businessType);
  if (modes.includes('book') && (businessType === 'salon' || businessType === 'barber' || businessType === 'spa')) {
    return 'book';
  }
  if (modes.includes('hire')) return 'hire';
  if (modes.includes('listings')) return 'listings';
  return modes[0] ?? 'shop';
}

/** Signup intent flags (sell = storefront, pro = services / bookings). */
export type SignupBusinessGoals = { sell?: boolean; pro?: boolean };

/**
 * Storefront modes for a new vendor — merges category defaults with signup goals.
 * "Sell + offer services" must include hire (and shop) or Bookings stays locked in the hub.
 */
export function inferBusinessModesForSignup(
  businessType?: string,
  goals?: SignupBusinessGoals | null,
  explicit?: BusinessMode[],
  directoryCategoryId?: string,
): BusinessMode[] {
  const modes = new Set<BusinessMode>(
    explicit ?? defaultModesForBusinessType(businessType, directoryCategoryId),
  );
  if (goals?.sell) modes.add('shop');
  if (goals?.pro) modes.add('hire');
  return [...modes];
}

export function primaryModeForModes(modes: BusinessMode[]): BusinessMode {
  if (modes.includes('book')) return 'book';
  if (modes.includes('hire')) return 'hire';
  if (modes.includes('listings')) return 'listings';
  return modes.includes('shop') ? 'shop' : modes[0] ?? 'shop';
}
